import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Loading } from 'react-daisyui'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { IconArrowLeft, IconPencil, IconPhoto, IconPlus, IconTrash } from '@tabler/icons-react'
import { Button } from '@/components/Button.tsx'
import { useClient } from '@/stores/useClient.ts'
import { ProfAccount } from '@/models/profAccount.ts'
import { ProfContact } from '@/models/profContact.ts'
import { BillingAddress } from '@/models/billingAddress.ts'
import ContactForm, { ContactFormSubmit, ContactFormValues } from '@/components/portal/ContactForm.tsx'
import { PROF_CONTACTS_QUERY_KEY, useProfContacts } from '@/components/portal/ProfContactSelect.tsx'
import { PROJECT_TEMPLATES_QUERY_KEY } from '@/components/portal/ProjectTemplateSelect.tsx'
import { DeleteConfirmModal, useDeleteConfirmModal } from '@/components/portal/DeleteConfirmModal.tsx'

function usageLabel(count: number): string {
  if (count === 0) return 'in keinem Projekt verwendet'
  return count === 1 ? 'in 1 Projekt verwendet' : `in ${count} Projekten verwendet`
}

function ContactAvatar({ image }: { image: string | null }) {
  return (
    <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center overflow-hidden shrink-0">
      {image
        ? <img key={image} src={image} alt="" className="w-full h-full object-cover" />
        : <IconPhoto size={18} className="opacity-40" />}
    </div>
  )
}

/**
 * Prefill for a new Marke: the current default contact, so only what differs has to be
 * changed. Name/company come from the billing address (the account sends it along, see
 * BillingAddressForm). The image and the internal label are deliberately left empty.
 */
function defaultContactPrefill(account: ProfAccount): ContactFormValues {
  const billing = account as ProfAccount & Partial<BillingAddress>
  return {
    company: billing.billing_company || '',
    first_name: billing.billing_first_name || '',
    last_name: billing.billing_last_name || '',
    contact_email: account.contact_email || '',
    contact_phone: account.contact_phone || '',
    contact_website: account.contact_website || '',
    contact_instagram: account.contact_instagram || '',
    contact_facebook: account.contact_facebook || '',
    contact_twitter: account.contact_twitter || '',
    contact_tiktok: account.contact_tiktok || '',
    contact_youtube: account.contact_youtube || '',
    contact_pinterest: account.contact_pinterest || '',
    contact_google: account.contact_google || '',
  }
}

/**
 * Settings → "Marken": alternative contacts a project can show instead of the account's
 * default contact. The default contact itself stays in the "Kontakt" tab and only appears
 * here as a fixed, non-deletable first row.
 */
export default function BrandsSettingsSection({ account }: { account: ProfAccount }) {
  const { createContact, updateContact, deleteContact } = useClient()
  const queryClient = useQueryClient()
  const { data: contacts, isLoading } = useProfContacts()
  const deleteModal = useDeleteConfirmModal()

  /** `'new'` = create form, a contact = edit form, `null` = list */
  const [editing, setEditing] = useState<ProfContact | 'new' | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<ProfContact | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  async function handleSubmit(data: ContactFormSubmit) {
    setIsSaving(true)
    try {
      if (editing === 'new') {
        await createContact(data)
        toast('Marke angelegt', { closeButton: true, autoClose: 3000 })
      } else if (editing) {
        await updateContact(editing.id, data)
        toast('Marke gespeichert', { closeButton: true, autoClose: 3000 })
      }
      queryClient.invalidateQueries({ queryKey: PROF_CONTACTS_QUERY_KEY })
      setEditing(null)
    } catch (e) {
      console.error('Error saving contact:', e)
      toast('Fehler beim Speichern', { closeButton: true, autoClose: 3000, type: 'error' })
      throw e
    } finally {
      setIsSaving(false)
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return
    setIsDeleting(true)
    try {
      await deleteContact(deleteTarget.id)
      queryClient.invalidateQueries({ queryKey: PROF_CONTACTS_QUERY_KEY })
      // Projects and templates using it fell back to the default contact on the server
      queryClient.invalidateQueries({ queryKey: ['project'] })
      queryClient.invalidateQueries({ queryKey: PROJECT_TEMPLATES_QUERY_KEY })
      deleteModal.close()
      setDeleteTarget(null)
      toast('Marke gelöscht', { closeButton: true, autoClose: 3000 })
    } catch (e) {
      console.error('Error deleting contact:', e)
      toast('Fehler beim Löschen', { closeButton: true, autoClose: 3000, type: 'error' })
    } finally {
      setIsDeleting(false)
    }
  }

  if (editing) {
    const contact = editing === 'new' ? null : editing
    return (
      <section className="bg-neutral p-5 max-w-xl">
        <button type="button" className="flex items-center gap-1 text-sm opacity-60 hover:opacity-100 mb-3"
          onClick={() => setEditing(null)}>
          <IconArrowLeft size={16} />
          Alle Marken
        </button>
        <h2 className="font-serif text-xl mb-4">{contact ? contact.name : 'Neue Marke'}</h2>
        <ContactForm
          key={contact?.id ?? 'new'}
          initialData={contact ?? defaultContactPrefill(account)}
          withIdentityFields
          onSubmit={handleSubmit}
          isLoading={isSaving}
          submitLabel={contact ? 'Speichern' : 'Marke anlegen'}
          onCancel={() => setEditing(null)}
        />
      </section>
    )
  }

  return (
    <section className="bg-neutral p-5 max-w-2xl">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <p className="text-sm opacity-60 max-w-md">
          Lege weitere Kontakte an, z. B. für einen anderen Bereich wie Tierfotografie. In den Projekteinstellungen wählst du,
          welcher Kontakt in der Galerie angezeigt wird.
        </p>
        <Button size="sm" color="accent" onClick={() => setEditing('new')}>
          <IconPlus size={16} />
          Neue Marke
        </Button>
      </div>

      <div className="flex flex-col divide-y divide-[var(--portal-border)] border-y border-[var(--portal-border)]">
        {/* Account default contact: fixed first row, edited in the "Kontakt" tab */}
        <div className="flex items-center gap-3 py-3 px-2">
          <ContactAvatar image={account.contact_image} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium flex items-center gap-2">
              Standard
              <span className="badge badge-sm badge-ghost">Standardkontakt</span>
            </p>
            <p className="text-xs opacity-50 truncate">
              {account.contact_email || 'Keine E-Mail hinterlegt'} · für alle Projekte ohne Marke
            </p>
          </div>
          <Link to="/portal/settings?tab=contact" className="text-sm opacity-60 hover:opacity-100 whitespace-nowrap">
            Bearbeiten →
          </Link>
        </div>

        {contacts?.map(contact => (
          <div key={contact.id} className="flex items-center gap-3 py-3 px-2 hover:bg-secondary group cursor-pointer"
            onClick={() => setEditing(contact)}>
            <ContactAvatar image={contact.contact_image} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{contact.name}</p>
              <p className="text-xs opacity-50 truncate">
                {[contact.company, contact.contact_email].filter(Boolean).join(' · ') || 'Keine Kontaktdaten'}
              </p>
              <p className="text-xs opacity-50">{usageLabel(contact.projects_count)}</p>
            </div>
            <div className="flex items-center shrink-0 md:opacity-0 md:group-hover:opacity-100">
              <Button size="sm" color="ghost" className="px-2 opacity-50 hover:opacity-100" title="Bearbeiten"
                onClick={e => { e.stopPropagation(); setEditing(contact) }}>
                <IconPencil size={16} />
              </Button>
              <Button size="sm" color="ghost" className="px-2 opacity-50 hover:opacity-100" title="Löschen"
                onClick={e => { e.stopPropagation(); setDeleteTarget(contact); deleteModal.show() }}>
                <IconTrash size={16} />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {isLoading && <Loading size="sm" className="mt-3" />}
      {contacts && contacts.length === 0 && (
        <p className="text-sm opacity-60 pt-3">Noch keine weiteren Marken angelegt.</p>
      )}

      <DeleteConfirmModal
        modalRef={deleteModal.modalRef}
        title="Marke löschen"
        message={deleteTarget
          ? `„${deleteTarget.name}" wirklich löschen? Projekte mit dieser Marke zeigen danach wieder den Standardkontakt.`
          : ''}
        isLoading={isDeleting}
        onConfirm={handleDelete}
      />
    </section>
  )
}
